// src/pages/notifications/NotificationsPage.jsx - UNIFIED VERSION (Notifications + Product Requests)
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, orderBy, getDocs, limit, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { getProductRequests } from '../../services/productRequestService';
import DashboardLayout from '../../components/layout/DashboardLayout';

const NotificationsPage = () => {
  const { currentUser, vendorProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Combined state for both collections
  const [allNotifications, setAllNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [unreadCount, setUnreadCount] = useState(0);

  // Check if vendor has order management
  const hasOrderManagement = vendorProfile?.businessType !== 'without-orders';

  // Load both notifications and product requests
  const loadAllNotifications = async () => {
    if (!currentUser?.uid) {
      console.log('❌ No current user for notifications');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('🔄 Loading all notifications for vendor:', currentUser.uid);

      const combinedNotifications = [];
      let totalUnread = 0;

      // 1. Load regular notifications (if collection exists)
      try {
        const notificationRef = collection(db, 'notifications');
        const notifQuery = query(
          notificationRef,
          where('vendorId', '==', currentUser.uid),
          orderBy('createdAt', 'desc'),
          limit(100)
        );

        const notifSnapshot = await getDocs(notifQuery);
        
        notifSnapshot.forEach((doc) => {
          const notification = {
            id: doc.id,
            source: 'notifications', // Mark source
            ...doc.data()
          };
          combinedNotifications.push(notification);
          
          if (!notification.read) {
            totalUnread++;
          }
        });

        console.log('✅ Loaded regular notifications:', notifSnapshot.size);
      } catch (notifError) {
        console.log('ℹ️ No notifications collection or access:', notifError.message);
      }

      // 2. Load product requests as notifications (if order management enabled)
      if (hasOrderManagement) {
        try {
          const requests = await getProductRequests(currentUser.uid);

          requests.forEach((request) => {
            // Convert product request to notification format
            const notification = {
              id: `request_${request.id}`,
              source: 'productRequests', // Mark source
              originalId: request.id, // Keep original ID for actions
              type: 'product_request',
              title: 'Product Request',
              message: `${request.mechanicName || 'A mechanic'} requested ${request.requestedQuantity || 1} x ${request.productName || 'product'}`,
              createdAt: request.createdAt,
              expiresAt: request.expiresAt,
              read: request.status !== 'pending', // Pending = unread
              productName: request.productName,
              mechanicName: request.mechanicName,
              requestedQuantity: request.requestedQuantity,
              status: request.status,
              urgencyLevel: request.urgencyLevel
            };

            combinedNotifications.push(notification);
            
            // Count pending requests as unread
            if (request.status === 'pending' && !isExpired(request)) {
              totalUnread++;
            }
          });

          console.log('✅ Loaded product requests:', requests.length);
        } catch (requestError) {
          console.error('❌ Error loading product requests:', requestError);
        }
      }

      // 3. Sort combined notifications by date
      combinedNotifications.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Newest first
      });

      console.log('✅ Combined notifications:', combinedNotifications.length, 'unread:', totalUnread);
      setAllNotifications(combinedNotifications);
      setUnreadCount(totalUnread);
    } catch (error) {
      console.error('❌ Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check if request is expired
  const isExpired = (request) => {
    if (!request.expiresAt) return false;
    
    try {
      const now = new Date();
      const expiry = request.expiresAt.toDate ? request.expiresAt.toDate() : new Date(request.expiresAt);
      return now > expiry;
    } catch (error) {
      return false;
    }
  };

  // Load notifications when component mounts or user changes
  useEffect(() => {
    if (currentUser?.uid) {
      loadAllNotifications();
    } else {
      setAllNotifications([]);
      setUnreadCount(0);
      setLoading(false);
    }
  }, [currentUser?.uid, hasOrderManagement]);

  // Check for URL params to set initial filter
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam && ['all', 'unread', 'read', 'product_requests', 'notifications'].includes(filterParam)) {
      setFilter(filterParam);
    }
  }, [searchParams]);

  // Mark notification as read
  const markAsRead = async (notification) => {
    try {
      if (notification.source === 'notifications') {
        // Handle regular notification
        const notificationRef = doc(db, 'notifications', notification.id);
        await updateDoc(notificationRef, {
          read: true,
          readAt: serverTimestamp()
        });
      } else if (notification.source === 'productRequests') {
        // Product requests don't have a "read" field, they're considered read when status changes
        // We'll just update local state for UI purposes
        console.log('Product request marked as viewed:', notification.originalId);
      }
      
      // Update local state immediately
      setAllNotifications(prev => 
        prev.map(notif => 
          notif.id === notification.id 
            ? { ...notif, read: true }
            : notif
        )
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));
      console.log('✅ Notification marked as read:', notification.id);
    } catch (error) {
      console.error('❌ Error marking notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      console.log('🔄 Marking all notifications as read...');
      
      for (const notification of allNotifications) {
        if (!notification.read && notification.source === 'notifications') {
          const notificationRef = doc(db, 'notifications', notification.id);
          await updateDoc(notificationRef, {
            read: true,
            readAt: serverTimestamp()
          });
        }
      }
      
      // Update local state
      setAllNotifications(prev => 
        prev.map(notif => ({ ...notif, read: true }))
      );
      setUnreadCount(0);
      
      console.log('✅ All notifications marked as read');
    } catch (error) {
      console.error('❌ Error marking all notifications as read:', error);
    }
  };

  // Filter and sort notifications
  const getFilteredNotifications = () => {
    let filtered = [...allNotifications];

    // Apply filter
    switch (filter) {
      case 'unread':
        filtered = filtered.filter(n => !n.read);
        break;
      case 'read':
        filtered = filtered.filter(n => n.read === true);
        break;
      case 'product_requests':
        filtered = filtered.filter(n => 
          n.type === 'product_request' || n.type === 'product-request'
        );
        break;
      case 'notifications':
        filtered = filtered.filter(n => n.source === 'notifications');
        break;
      case 'all':
      default:
        // No filtering needed
        break;
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt);
      const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt);
      
      if (sortBy === 'oldest') {
        return aTime - bTime; // Oldest first
      } else {
        return bTime - aTime; // Newest first (default)
      }
    });

    return filtered;
  };

  const filteredNotifications = getFilteredNotifications();

  // Format notification time
  const formatNotificationTime = (timestamp) => {
    if (!timestamp) return 'Recently';
    
    try {
      const now = new Date();
      const notificationTime = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const diffTime = Math.abs(now - notificationTime);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      const diffMinutes = Math.floor(diffTime / (1000 * 60));
      
      if (diffDays > 7) {
        return notificationTime.toLocaleDateString();
      } else if (diffDays > 0) {
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      } else if (diffHours > 0) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      } else if (diffMinutes > 0) {
        return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
      } else {
        return 'Just now';
      }
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'Recently';
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    console.log('🔔 Notification clicked:', notification);
    
    try {
      // Mark as read if unread
      if (!notification.read) {
        await markAsRead(notification);
      }
      
      // Navigate to relevant page based on type and source
      if (notification.type === 'product_request' || notification.type === 'product-request') {
        navigate('/orders?tab=product-requests');
      } else if (notification.type === 'order' && notification.orderId) {
        navigate(`/orders/${notification.orderId}`);
      } else if (notification.type === 'message' && notification.chatId) {
        navigate(`/communication/${notification.chatId}`);
      } else if (notification.type === 'product' && notification.productId) {
        navigate(`/inventory/${notification.productId}`);
      } else if (notification.type === 'customer' && notification.customerId) {
        navigate(`/customers/${notification.customerId}`);
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  // Get notification icon
  const getNotificationIcon = (type, source) => {
    const iconClass = "h-6 w-6";
    const containerClass = "w-12 h-12 rounded-full flex items-center justify-center";

    switch (type) {
      case 'product_request':
      case 'product-request':
        return (
          <div className={`${containerClass} bg-blue-100`}>
            <svg className={`${iconClass} text-blue-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
        );
      case 'order':
        return (
          <div className={`${containerClass} bg-green-100`}>
            <svg className={`${iconClass} text-green-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          </div>
        );
      case 'message':
        return (
          <div className={`${containerClass} bg-orange-100`}>
            <svg className={`${iconClass} text-orange-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </div>
        );
      case 'payment':
        return (
          <div className={`${containerClass} bg-yellow-100`}>
            <svg className={`${iconClass} text-yellow-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
          </div>
        );
      case 'system':
        return (
          <div className={`${containerClass} bg-purple-100`}>
            <svg className={`${iconClass} text-purple-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className={`${containerClass} bg-gray-100`}>
            <svg className={`${iconClass} text-gray-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  // Get priority badge for urgent notifications
  const getPriorityBadge = (notification) => {
    if (notification.type === 'product_request' || notification.type === 'product-request') {
      if (notification.expiresAt) {
        try {
          const now = new Date();
          const expiry = notification.expiresAt.toDate ? notification.expiresAt.toDate() : new Date(notification.expiresAt);
          const hoursLeft = (expiry - now) / (1000 * 60 * 60);
          
          if (hoursLeft <= 0) {
            return (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                Expired
              </span>
            );
          } else if (hoursLeft <= 1) {
            return (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Urgent
              </span>
            );
          } else if (hoursLeft <= 6) {
            return (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                Soon
              </span>
            );
          }
        } catch (error) {
          console.error('Error calculating priority:', error);
        }
      }
      
      // Check urgency level from product request
      if (notification.urgencyLevel) {
        const urgencyColors = {
          low: 'bg-green-100 text-green-800',
          normal: 'bg-blue-100 text-blue-800',
          high: 'bg-yellow-100 text-yellow-800',
          urgent: 'bg-red-100 text-red-800'
        };
        
        return (
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${urgencyColors[notification.urgencyLevel] || urgencyColors.normal}`}>
            {notification.urgencyLevel.charAt(0).toUpperCase() + notification.urgencyLevel.slice(1)}
          </span>
        );
      }
    }
    return null;
  };

  // Get count for each filter
  const getFilterCount = (filterType) => {
    if (!allNotifications.length) return 0;
    
    switch (filterType) {
      case 'unread':
        return allNotifications.filter(n => !n.read).length;
      case 'read':
        return allNotifications.filter(n => n.read === true).length;
      case 'product_requests':
        return allNotifications.filter(n => 
          n.type === 'product_request' || n.type === 'product-request'
        ).length;
      case 'notifications':
        return allNotifications.filter(n => n.source === 'notifications').length;
      default:
        return allNotifications.length;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading notifications...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
              <p className="mt-2 text-gray-600">
                Stay updated with your latest activities, messages, and product requests
              </p>
            </div>
            
            <div className="flex space-x-3">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
                >
                  Mark All as Read ({unreadCount})
                </button>
              )}
              
              <button
                onClick={loadAllNotifications}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
              >
                <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Filters and Sort */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-8">
            {/* Filter Tabs */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 flex items-center space-x-2 ${
                  filter === 'all'
                    ? 'bg-orange-100 text-orange-700 border-2 border-orange-200 shadow-sm'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50 border-2 border-transparent'
                }`}
              >
                <span>All</span>
                <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-gray-500 bg-gray-200 rounded-full">
                  {getFilterCount('all')}
                </span>
              </button>
              
              <button
                onClick={() => setFilter('unread')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 flex items-center space-x-2 ${
                  filter === 'unread'
                    ? 'bg-orange-100 text-orange-700 border-2 border-orange-200 shadow-sm'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50 border-2 border-transparent'
                }`}
              >
                <span>Unread</span>
                {getFilterCount('unread') > 0 && (
                  <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
                    {getFilterCount('unread')}
                  </span>
                )}
              </button>
              
              <button
                onClick={() => setFilter('read')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 flex items-center space-x-2 ${
                  filter === 'read'
                    ? 'bg-orange-100 text-orange-700 border-2 border-orange-200 shadow-sm'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50 border-2 border-transparent'
                }`}
              >
                <span>Read</span>
                <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-gray-500 bg-gray-200 rounded-full">
                  {getFilterCount('read')}
                </span>
              </button>
              
              <button
                onClick={() => setFilter('product_requests')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 flex items-center space-x-2 ${
                  filter === 'product_requests'
                    ? 'bg-orange-100 text-orange-700 border-2 border-orange-200 shadow-sm'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50 border-2 border-transparent'
                }`}
              >
                <span>Product Requests</span>
                {getFilterCount('product_requests') > 0 && (
                  <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-blue-600 bg-blue-100 rounded-full">
                    {getFilterCount('product_requests')}
                  </span>
                )}
              </button>

              <button
                onClick={() => setFilter('notifications')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 flex items-center space-x-2 ${
                  filter === 'notifications'
                    ? 'bg-orange-100 text-orange-700 border-2 border-orange-200 shadow-sm'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50 border-2 border-transparent'
                }`}
              >
                <span>System Notifications</span>
                {getFilterCount('notifications') > 0 && (
                  <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-purple-600 bg-purple-100 rounded-full">
                    {getFilterCount('notifications')}
                  </span>
                )}
              </button>
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center space-x-3">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Sort by:
              </label>
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none bg-white border-2 border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm font-medium text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors duration-200 min-w-[140px]"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Debug info */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            <strong>Debug:</strong> {allNotifications.length} total ({getFilterCount('notifications')} notifications + {getFilterCount('product_requests')} requests), {unreadCount} unread, showing {filteredNotifications.length} after filter "{filter}"
          </div>
        )}

        {/* Notifications List */}
        <div className="space-y-4">
          {filteredNotifications.length > 0 ? (
            filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`bg-white rounded-lg shadow-sm border-2 transition-all duration-200 hover:shadow-md cursor-pointer transform hover:-translate-y-1 ${
                  notification.read 
                    ? 'border-gray-200 hover:border-gray-300' 
                    : 'border-orange-200 bg-orange-50 hover:border-orange-300'
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="p-6">
                  <div className="flex items-start">
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      {getNotificationIcon(notification.type, notification.source)}
                    </div>
                    
                    {/* Content */}
                    <div className="ml-4 flex-1">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className={`text-lg font-semibold ${
                              notification.read ? 'text-gray-900' : 'text-gray-900'
                            }`}>
                              {notification.title || 'Notification'}
                              </h3>
                           {getPriorityBadge(notification)}
                           <span className={`text-xs px-2 py-1 rounded-full ${
                             notification.source === 'productRequests' 
                               ? 'bg-blue-100 text-blue-700' 
                               : 'bg-purple-100 text-purple-700'
                           }`}>
                             {notification.source === 'productRequests' ? 'Request' : 'System'}
                           </span>
                         </div>
                         
                         <p className="text-gray-600 text-sm leading-relaxed mb-3">
                           {notification.message || 'No message available'}
                         </p>
                         
                         {/* Additional info based on type and source */}
                         <div className="space-y-1">
                           {notification.type === 'order' && notification.orderNumber && (
                             <p className="text-xs text-gray-500 font-medium">
                               Order: #{notification.orderNumber}
                             </p>
                           )}
                           
                           {notification.type === 'message' && notification.customerName && (
                             <p className="text-xs text-gray-500 font-medium">
                               From: {notification.customerName}
                             </p>
                           )}
                           
                           {(notification.type === 'product_request' || notification.type === 'product-request') && (
                             <div className="text-xs text-gray-500 space-y-1">
                               {notification.mechanicName && (
                                 <p><strong>Mechanic:</strong> {notification.mechanicName}</p>
                               )}
                               {notification.productName && (
                                 <p><strong>Product:</strong> {notification.productName}</p>
                               )}
                               {notification.requestedQuantity && (
                                 <p><strong>Quantity:</strong> {notification.requestedQuantity}</p>
                               )}
                               {notification.expiresAt && (
                                 <p><strong>Expires:</strong> {formatNotificationTime(notification.expiresAt)}</p>
                               )}
                               {notification.status && (
                                 <p><strong>Status:</strong> 
                                   <span className={`ml-1 px-2 py-0.5 rounded text-xs font-medium ${
                                     notification.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                     notification.status === 'accepted' ? 'bg-green-100 text-green-800' :
                                     notification.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                     'bg-gray-100 text-gray-800'
                                   }`}>
                                     {notification.status.charAt(0).toUpperCase() + notification.status.slice(1)}
                                   </span>
                                 </p>
                               )}
                             </div>
                           )}
                         </div>
                       </div>
                       
                       {/* Time and read status */}
                       <div className="ml-4 flex-shrink-0 text-right">
                         <p className="text-xs text-gray-500 font-medium">
                           {formatNotificationTime(notification.createdAt)}
                         </p>
                         {!notification.read && (
                           <div className="mt-2 flex justify-end">
                             <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
                           </div>
                         )}
                       </div>
                     </div>
                   </div>
                 </div>
               </div>
             </div>
           ))
         ) : (
           /* Empty State */
           <div className="text-center py-16">
             <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
               <svg className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
               </svg>
             </div>
             <h3 className="text-xl font-semibold text-gray-900 mb-2">No notifications found</h3>
             <p className="text-gray-500 mb-6 max-w-md mx-auto">
               {filter === 'unread' 
                 ? 'You\'re all caught up! No unread notifications at the moment.'
                 : filter === 'read'
                 ? 'No read notifications to display.'
                 : filter === 'product_requests'
                 ? 'No product request notifications yet.'
                 : filter === 'notifications'
                 ? 'No system notifications yet.'
                 : 'You don\'t have any notifications yet. They\'ll appear here when you receive them.'}
             </p>
             <div className="flex justify-center space-x-3">
               <Link
                 to="/dashboard"
                 className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors duration-200"
               >
                 Go to Dashboard
               </Link>
               <button
                 onClick={loadAllNotifications}
                 className="inline-flex items-center px-6 py-3 border border-gray-300 text-sm font-medium rounded-lg shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors duration-200"
               >
                 Refresh
               </button>
             </div>
           </div>
         )}
       </div>

       {/* Statistics Summary */}
       {allNotifications.length > 0 && (
         <div className="mt-8 bg-gray-50 rounded-lg p-6">
           <h3 className="text-lg font-medium text-gray-900 mb-4">Summary</h3>
           <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
             <div className="text-center">
               <div className="text-2xl font-bold text-gray-900">{getFilterCount('all')}</div>
               <div className="text-sm text-gray-500">Total</div>
             </div>
             <div className="text-center">
               <div className="text-2xl font-bold text-red-600">{getFilterCount('unread')}</div>
               <div className="text-sm text-gray-500">Unread</div>
             </div>
             <div className="text-center">
               <div className="text-2xl font-bold text-blue-600">{getFilterCount('product_requests')}</div>
               <div className="text-sm text-gray-500">Product Requests</div>
             </div>
             <div className="text-center">
               <div className="text-2xl font-bold text-purple-600">{getFilterCount('notifications')}</div>
               <div className="text-sm text-gray-500">System Notifications</div>
             </div>
           </div>

           {/* Quick Actions */}
           <h4 className="text-md font-medium text-gray-900 mb-3">Quick Actions</h4>
           <div className="flex flex-wrap gap-3">
             <Link
               to="/orders?tab=product-requests"
               className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 transition-colors"
             >
               <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
               </svg>
               View Product Requests
             </Link>
             
             <Link
               to="/orders"
               className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 transition-colors"
             >
               <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
               </svg>
               View All Orders
             </Link>
             
             <Link
               to="/communication"
               className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-orange-700 bg-orange-100 hover:bg-orange-200 transition-colors"
             >
               <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
               </svg>
               View Messages
             </Link>
             
             <Link
               to="/inventory"
               className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 transition-colors"
             >
               <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
               </svg>
               Manage Inventory
             </Link>
           </div>
         </div>
       )}
     </div>
   </DashboardLayout>
 );
};

export default NotificationsPage;